'use client';

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional custom fallback. Defaults to a minimal placeholder div. */
  fallback?: ReactNode;
  /** Label used in dev-mode console warnings */
  componentName?: string;
  /** When true, renders null instead of a placeholder (for purely decorative elements) */
  silent?: boolean;
}

interface State {
  hasError: boolean;
}

/**
 * Lightweight error boundary for scroll animations, 3D renders, and
 * interactive components. Prevents a single widget crash from breaking
 * the entire page. Renders a neutral placeholder so layout is preserved.
 */
class AnimationErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const name = this.props.componentName ?? 'AnimatedComponent';
    console.warn(
      `[AnimationErrorBoundary] "${name}" crashed and was replaced with a fallback to protect the page.`,
      error,
      errorInfo
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      if (this.props.silent) {
        return null;
      }
      // Neutral placeholder that preserves layout without showing an error
      return (
        <div
          className="w-full rounded-xl bg-white/5 border border-white/10"
          aria-hidden="true"
          style={{ minHeight: '4rem' }}
        />
      );
    }
    return this.props.children;
  }
}

export default AnimationErrorBoundary;
